import React, { useEffect, useState, useCallback } from "react";
import axios from "utils/axiosConfig";
import { useSelector } from "react-redux";
import uri from "../../features/config";
import AdminLayout from "../AdminLayout";
import {
  HiOutlineShieldCheck,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineLightningBolt,
  HiOutlineRefresh,
} from "react-icons/hi";
import { useToast } from "../../components/Toast";

const AgentProposals = () => {
  const toast = useToast();
  const { user } = useSelector((state) => state.auth);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${uri}/api/admin/agents/proposals`, {
        headers: { "x-user-email": user?.data?.email },
      });
      setProposals(response.data.data);
    } catch (error) {
      toast.error("Intelligence synchronization failed");
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    if (user?.data?.email) fetchProposals();
  }, [user, fetchProposals]);

  const updateProposalStatus = async (id, status) => {
    try {
      await axios.put(
        `${uri}/api/admin/agents/proposals/${id}`,
        { status },
        { headers: { "x-user-email": user?.data?.email } }
      );
      toast.success(`Protocol ${status}: Signal Broadcasted`);
      fetchProposals();
    } catch (error) {
      toast.error("Signal broadcast failed");
    }
  };

  const statusColors = {
    pending:
      "bg-amber-500/10 text-amber-400 border-amber-500/20 shadow-[0_0_10px_rgba(245,158,11,0.1)]",
    approved:
      "bg-green-500/10 text-green-400 border-green-500/20 shadow-[0_0_10px_rgba(34,197,94,0.1)]",
    rejected:
      "bg-red-500/10 text-red-500 border-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.1)]",
  };

  return (
    <AdminLayout>
      <div className="space-y-12 animate-fadeIn">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-[10px] font-black text-purple-400 uppercase tracking-widest">
              Neural_Network_Status: Monitoring
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
              Intelligence <span className="text-purple-400">Review</span>
            </h1>
            <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-[10px]">
              Human-in-the-Loop oversight for VR Staff autonomous proposals.
            </p>
          </div>
          <button
            onClick={fetchProposals}
            className="p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all text-purple-400 group"
          >
            <HiOutlineRefresh
              size={24}
              className={
                loading
                  ? "animate-spin"
                  : "group-hover:rotate-180 transition-transform duration-500"
              }
            />
          </button>
        </header>

        <div className="grid grid-cols-1 gap-8">
          {proposals.map((proposal) => (
            <div
              key={proposal._id}
              className="group relative bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-[2.5rem] p-10 hover:border-purple-500/30 transition-all duration-500 overflow-hidden"
            >
              {/* Status Glow */}
              <div
                className={`absolute top-0 right-0 w-32 h-32 blur-[80px] opacity-10 pointer-events-none ${
                  proposal.status === "pending"
                    ? "bg-amber-500"
                    : proposal.status === "approved"
                    ? "bg-green-500"
                    : "bg-red-500"
                }`}
              />

              <div className="flex flex-col md:flex-row gap-10 relative z-10">
                {/* Agent Identity */}
                <div className="flex flex-col items-center md:items-start md:w-48 text-center md:text-left">
                  <div className="p-5 bg-purple-500/10 rounded-3xl border border-purple-500/20 text-purple-400 mb-6 shadow-inner animate-pulse">
                    <HiOutlineShieldCheck size={32} />
                  </div>
                  <h4 className="text-xl font-black text-white italic truncate w-full">
                    {proposal.agentName}
                  </h4>
                  <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mt-1">
                    VR_Staff_Identifier
                  </p>
                </div>

                {/* Proposal Content */}
                <div className="flex-1 space-y-8">
                  <div className="flex items-center justify-between">
                    <span className="px-4 py-1.5 bg-white/5 border border-white/10 text-white text-[9px] font-black rounded-xl uppercase tracking-[0.2em] italic">
                      Type: {proposal.type}
                    </span>
                    <div
                      className={`px-4 py-1.5 border rounded-xl text-[9px] font-black uppercase tracking-[0.3em] italic ${
                        statusColors[proposal.status]
                      }`}
                    >
                      {proposal.status}
                    </div>
                  </div>

                  <div className="p-8 bg-white/[0.02] border border-white/5 rounded-3xl">
                    <div className="flex items-start gap-4 mb-4">
                      <HiOutlineLightningBolt
                        className="text-cyan-400 mt-1"
                        size={18}
                      />
                      <p className="text-white font-bold text-sm tracking-tight leading-relaxed">
                        {proposal.data.reasoning ||
                          "Autonomous logic sequence initiated based on live telemetry analysis."}
                      </p>
                    </div>
                    {proposal.data.suggestedChange && (
                      <div className="mt-6 p-6 bg-cyan-500/5 border border-cyan-500/10 rounded-2xl">
                        <p className="text-[10px] text-cyan-500 font-black uppercase tracking-widest mb-2 italic">
                          Suggested_Action
                        </p>
                        <p className="text-lg font-black text-white italic font-mono uppercase truncate">
                          {proposal.data.suggestedChange}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions (Only for pending) */}
                  {proposal.status === "pending" && (
                    <div className="flex flex-wrap gap-4">
                      <button
                        onClick={() =>
                          updateProposalStatus(proposal._id, "approved")
                        }
                        className="flex-1 md:flex-none flex items-center justify-center gap-3 px-8 py-4 bg-green-500 text-black font-black rounded-2xl hover:bg-green-400 transition-all shadow-[0_0_20px_rgba(34,197,94,0.2)] transform active:scale-95"
                      >
                        <HiOutlineCheck size={20} />
                        <span className="uppercase tracking-widest text-xs italic">
                          Authorize Protocol
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          updateProposalStatus(proposal._id, "rejected")
                        }
                        className="flex-1 md:flex-none flex items-center justify-center gap-3 px-8 py-4 bg-white/5 text-red-500 font-black rounded-2xl hover:bg-white/10 border border-red-500/20 transition-all transform active:scale-95"
                      >
                        <HiOutlineX size={20} />
                        <span className="uppercase tracking-widest text-xs italic">
                          Abort Signal
                        </span>
                      </button>
                    </div>
                  )}

                  {proposal.status !== "pending" && (
                    <div className="pt-4 flex items-center gap-3 opacity-50">
                      <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest italic">
                        Reviewed By: {proposal.reviewedBy || "SYSTEM_ORACLE"}
                      </p>
                      <span className="text-gray-700">|</span>
                      <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest italic">
                        TS: {new Date(proposal.reviewedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {proposals.length === 0 && !loading && (
            <div className="py-32 text-center bg-white/5 rounded-[3rem] border border-white/10 border-dashed">
              <p className="text-gray-500 font-black uppercase tracking-[0.3em] italic text-xs">
                No active intelligence proposals detected.
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AgentProposals;
